import { track } from "@/lib/analytics";

/**
 * Sign-in, sign-up and sign-out events across the full-page form post (NG-2068).
 *
 * The auth endpoints are form POSTs that navigate away and come back
 * authenticated, so an event fired at submit would leave anonymous and would
 * likely be lost to the unload. Submitting records which event and method are
 * pending, and the app sends it once it has identified the user it belongs to.
 * A failed attempt comes back to the sign-in page, and the next submit
 * overwrites what it left.
 */

type TAuthEvent = "signed_in" | "signed_up";
export type TAuthMethod = "password" | "magic_code" | "sso";

const PENDING_AUTH_EVENT_KEY = "cognota:pending-auth-event";
const EVENTS: readonly TAuthEvent[] = ["signed_in", "signed_up"];
const METHODS: readonly TAuthMethod[] = ["password", "magic_code", "sso"];

export function markPendingAuthEvent(event: TAuthEvent, method: TAuthMethod): void {
  try {
    sessionStorage.setItem(PENDING_AUTH_EVENT_KEY, JSON.stringify({ event, method }));
  } catch (error) {
    // Private mode or a full quota: the sign-in itself is unaffected.
    console.warn("[analytics] could not record a pending sign-in event", { event, method, error });
  }
}

/** Send and clear the pending sign-in event, if there is one. */
export function flushPendingAuthEvent(): void {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(PENDING_AUTH_EVENT_KEY);
    if (raw) sessionStorage.removeItem(PENDING_AUTH_EVENT_KEY);
  } catch (error) {
    console.warn("[analytics] could not read a pending sign-in event", { error });
    return;
  }
  if (!raw) return;
  try {
    const { event, method } = JSON.parse(raw) as { event?: unknown; method?: unknown };
    // Matched against the closed sets: the value sits in storage anything can write.
    if (EVENTS.includes(event as TAuthEvent) && METHODS.includes(method as TAuthMethod)) {
      track(event as TAuthEvent, { method });
    }
  } catch (error) {
    console.warn("[analytics] dropped an unreadable pending sign-in event", { error });
  }
}

// --- Sign-out ---------------------------------------------------------------
//
// The sign-out POST navigates away too, so it is two events.
// `sign_out_started` fires as the POST goes out, while the person is still
// identified. `signed_out` fires on the next load, once the app has seen that the
// session is gone. If the next load still finds the person signed in, the
// sign-out was interrupted: the pending record is discarded and nothing is sent.
//
// By then the person is no longer identified, so the record keeps their user id
// (only the id) until `signed_out` is sent. The caller uses it to send the event
// as that person, and the record is removed as soon as it is read.

export type TSignOutReason = "user" | "idle" | "deactivated" | "wrong_account";

const PENDING_SIGN_OUT_KEY = "cognota:pending-sign-out";
const SIGN_OUT_REASONS: readonly TSignOutReason[] = [
  "user",
  "idle",
  "deactivated",
  "wrong_account",
];

export function startSignOutEvent(reason: TSignOutReason, userId: string | null): void {
  track("sign_out_started", { reason });
  try {
    sessionStorage.setItem(PENDING_SIGN_OUT_KEY, JSON.stringify({ reason, user_id: userId }));
  } catch (error) {
    console.warn("[analytics] could not record a pending sign-out", { reason, error });
  }
}

function takePendingSignOut(): { reason: TSignOutReason; userId: string | null } | null {
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(PENDING_SIGN_OUT_KEY);
    if (raw) sessionStorage.removeItem(PENDING_SIGN_OUT_KEY);
  } catch (error) {
    console.warn("[analytics] could not read a pending sign-out", { error });
    return null;
  }
  if (!raw) return null;
  try {
    const { reason, user_id } = JSON.parse(raw) as { reason?: unknown; user_id?: unknown };
    // Matched against the closed set: the value sits in storage anything can write.
    if (!SIGN_OUT_REASONS.includes(reason as TSignOutReason)) return null;
    return {
      reason: reason as TSignOutReason,
      userId: typeof user_id === "string" && user_id ? user_id : null,
    };
  } catch (error) {
    console.warn("[analytics] dropped an unreadable pending sign-out", { error });
    return null;
  }
}

/**
 * The session is gone: a pending sign-out completed. `asUser` lets the caller
 * send the event as the person who signed out (Userpilot re-identifies by the
 * stored id around the send). Without it, or without a stored id, the event is
 * sent as whoever is current.
 */
export function completePendingSignOut(asUser?: (userId: string, send: () => void) => void): void {
  const pending = takePendingSignOut();
  if (!pending) return;
  const send = () => track("signed_out", { reason: pending.reason });
  if (asUser && pending.userId) {
    asUser(pending.userId, send);
  } else {
    send();
  }
}

/** The person is still signed in: a pending sign-out never completed. */
export function discardPendingSignOut(): void {
  takePendingSignOut();
}
